import { useEffect, useState } from "react";
import { getJobRequirementById } from "@endpoints/hiringCompany/requirements";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { colors } from "@utils/theme";
import {
  JobRequirementCloseReason,
  JobType,
  WorkPlaceOptions,
} from "@utils/constants";
import { getJobStatusLabel } from "@utils/helperFunction";
import { ICreateJobRequirementResponse } from "@endpoints/types/jobRequirement.type";
import CloseJobDialog from "../components/CloseJobDialog";

const JobDetails: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [details, setDetails] = useState<ICreateJobRequirementResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showCloseDialog, setShowCloseDialog] = useState(false);

  const fetchDetails = async () => {
    try {
      const { success, data } = await getJobRequirementById({
        jobRequirementId: params?.id,
      });
      if (success) {
        setDetails(data);
      }
      console.log(data, "data");
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col rounded-lg bg-white w-full flex-grow p-6 justify-center items-center">
        <LoadingAnimation color={colors.PrimaryBlack} />
      </div>
    );
  }

  const jobFields = [
    { label: "Job title", value: details?.title },
    { label: "Job reference", value: details?.refNo },
    { label: "Job location", value: details?.location },
    { label: "Industry type", value: details?.industryType },
    {
      label: "Work place",
      value: WorkPlaceOptions.find((x) => details?.workPlaceOption === x.value)
        ?.label,
    },
    {
      label: "Job type",
      value: JobType.find((x) => details?.jobType === x.value)?.label,
    },
    {
      label: "Salary Range",
      value: `${details?.salary.min}${
        details?.salary.max && " " + details?.salary.max
      }`,
    },
    {
      label: "Job published date",
      value: dayjs(details?.createdAt).format("Do MMMM YYYY"),
    },
    { label: "Job Status", value: getJobStatusLabel(details?.status) },
  ];

  const handleEditJobPost = () => {
    navigate(`/requirements/job/edit/${params?.id}`);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 p-4 md:p-8 max-w-5xl mx-auto w-3/4">
      {/* Action container */}
      <div className="flex items-center justify-end gap-2 mb-5">
        <button
          onClick={handleEditJobPost}
          className="font-medium text-xs rounded-md border py-1.5 px-4 hover:bg-gray-100"
        >
          Edit job post
        </button>
        {!details?.closeReason ? (
          <button
            onClick={() => setShowCloseDialog(true)}
            className="font-medium text-xs rounded-md py-1.5 px-4 bg-red-500 text-white hover:bg-red-600"
          >
            Close job post
          </button>
        ) : (
          <div className="font-medium text-xs bg-red-600 text-white rounded-md px-4 py-1.5">
            Job Closed
          </div>
        )}
      </div>
      <div className="flex flex-col flex-1 min-h-0 overflow-y-scroll no-scrollbar">
        <div className="flex flex-col flex-1 gap-5 w-3/4 self-center">
          {jobFields.map((field, idx) => (
            <div key={idx} className="flex ">
              <span className="min-w-[160px] font-semibold text-xs">
                {field.label} :
              </span>
              <span className="font-normal text-xs">{field.value}</span>
            </div>
          ))}
          <div className="flex items-start">
            <span className="min-w-[160px] font-semibold text-xs">
              Job Description :
            </span>
            <p
              className="font-normal text-xs"
              dangerouslySetInnerHTML={{ __html: details?.description }}
            />
          </div>
        </div>

        <hr className="my-8 border-t border-dashed border-gray-300" />
        <div className="flex flex-col w-3/4 self-center">
          <h3 className="font-semibold text-xs text-primary">
            Skills required :
          </h3>
          <div className="h-full flex flex-1 flex-col mt-3">
            {(details?.skills?.length ?? 0) > 0 && (
              <div className="grid space-x-3 py-2.5 items-center w-full justify-center relative grid-cols-[1.7fr_0.68fr_0.58fr_1fr]">
                {["Skill", "Rating", "Years", "When", ""].map((label) => (
                  <span
                    key={label}
                    className="text-xs font-semibold text-gray-700 flex-1"
                  >
                    {label}
                  </span>
                ))}
              </div>
            )}
            <div className="overflow-auto h-full w-full">
              {details?.skills?.map((skill) => (
                <div
                  key={skill?.lastUsed}
                  className="grid space-x-3 py-2.5 items-center w-full justify-center relative grid-cols-[1.7fr_0.68fr_0.58fr_1fr]"
                >
                  {[
                    skill.title,
                    `${skill?.rating}/10`,
                    skill.years,
                    skill.lastUsed,
                  ].map((value, index) => (
                    <div
                      key={index}
                      className="border border-gray-100 bg-[#FAFAFA] px-4 py-2.5 text-xs font-medium text-primary rounded flex flex-1"
                    >
                      {value}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* CloseJobDialog */}
      <CloseJobDialog
        openDialog={showCloseDialog}
        setOpenDialog={setShowCloseDialog}
        onConfirmed={(closeReason) =>
          setDetails((prev) => {
            return { ...prev, closeReason };
          })
        }
      />
    </div>
  );
};

export default JobDetails;
